/*
 * @providesModule CashDisabledLocationController
 * This is a controller for cash disabled location options
 */

import React from 'react';

import AWSManager from 'AWSManager';
import CurrentUserStore from 'CurrentUserStore';
import GlobalEventEmitter from 'GlobalEventEmitter';
import {_m} from 'INTL';
import LoadingIndicator from 'LoadingIndicator';
import LocationPermissionController from 'LocationPermissionController';
import _ from 'lodash';
import ModalNavigationController from 'ModalNavigationController';
import Preferences from 'Preferences';
import {Navigator} from 'react-native-deprecated-custom-components';
import Markdown from 'react-native-markdown-renderer';
import RESTStore from 'RESTStore';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';
import SKZConstants from 'SKZConstants';
import StaticWarningModal from 'StaticWarningModal';
import styles from 'StaticCashDisabledModalStyle';
import {NativeModules, StyleSheet, Platform} from 'react-native';

const SKZAPIManager = NativeModules.SKZAPIManager;
const SkillzModule = NativeModules.SkillzModule;
const {fontRegular, fontSizeH1, fontSizeH2, fontSizeH3, fontSizeH4} = SkillzStyle.fullStyles;

export const MarkdownStyle = StyleSheet.create({
  text: {
    ...Sizes.select({
      smallPhone: fontSizeH4,
      largePhone: fontSizeH3,
      landscapeTablet: fontSizeH2,
      portraitTablet: fontSizeH1,
    }),
    ...fontRegular,
    color: '#4A4A4A',
    paddingHorizontal: 25,
    textAlign: 'center',
    marginBottom: 17.5,
    marginHorizontal: 1,
  },
});

class CashDisabledLocationController {
  private checkedLocation = false;

  async checkForCashDisabledLocation(
    navigator: Navigator,
    useCache = this.checkedLocation // Once we've checked the location, assume we'll use the cached value
  ): Promise<boolean> {
    try {
      const userProfile = await CurrentUserStore.fetchUserProfile(useCache);

      const hasSeenWarning =
        Preferences.get(SKZConstants.HAS_SEEN_CASH_DISABLED_LOCATION_WARNING) || false;

      if (hasSeenWarning && userProfile.cash_depositor) {
        return true;
      }
      const result = await this.executeCheckForCashDisabledLocation(navigator, useCache);
      this.checkedLocation = true;

      return result;
    } catch (e) {
      return false;
    }
  }

  async executeCheckForCashDisabledLocation(
    navigator: Navigator,
    useCache = false
  ): Promise<boolean> {
    try {
      let response = await this.queryServerForCashDisabledLocation(useCache);

      if (!response.cash_enabled) {
        AWSManager.trackEvent('CASH_DISABLED_INITIAL_CHECK_FAILED');
        let permissionGranted = false;

        try {
          {
            Platform.OS === 'web' && LoadingIndicator.hide();
          }
          permissionGranted = await LocationPermissionController.promptForLocation(
            false,
            this._locationModalCallback,
            true
          );
          {
            Platform.OS === 'web' && LoadingIndicator.show();
          }
        } catch (e) {
          permissionGranted = false;
          AWSManager.trackEvent('CASH_DISABLED_INITIAL_LOCATION_PERMISSION_DENIED');
        }

        if (!permissionGranted) {
          navigator.popToTop();
          return false;
        }

        AWSManager.trackEvent('CASH_DISABLED_INITIAL_LOCATION_PERMISSION_GRANTED');
        response = await this.queryServerForCashDisabledLocation(false); // Purposely avoid using the cache here

        if (!response.cash_enabled) {
          AWSManager.trackEvent('CASH_DISABLED_SECOND_CHECK_FAILED');
          return await this.displayCashDisabledLocationWarning(navigator);
        }
      } else {
        AWSManager.trackEvent('CASH_DISABLED_INITIAL_CHECK_PASSED');
      }
    } catch (e) {
      console.debug('cash enabled location check failed');
    }
    return true;
  }

  async queryServerForCashDisabledLocation(useCache = false): Promise<{
    cash_enabled: boolean;
    criteria: string;
  }> {
    const response = await RESTStore.getResponseWithOptions(
      'store/cash_enabled_location',
      null,
      SKZAPIManager.OnError.failSilently,
      {useCache}
    );

    const prefSetting = _.toNumber(Preferences.get(SKZConstants.SIMULATE_CASH_DISABLED_KEY));
    if (
      prefSetting == SKZConstants.SIMULATE_CASH_DISABLED_LOCATION ||
      (prefSetting == SKZConstants.SIMULATE_CASH_DISABLED_IP && response.criteria === 'ip')
    ) {
      response.cash_enabled = false;
    }
    return response;
  }

  displayCashDisabledLocationWarning(navigator: Navigator): Promise<boolean> {
    return new Promise((resolve: (boolean) => void, reject: () => void) => {
      const modal = (
        <StaticWarningModal
          renderCustomView={this._renderCustomView.bind(this)}
          titleTextStyling={styles.title}
          buttonTextStyling={styles.buttonText}
          buttonContainerStyling={styles.buttonContainer}
          title={_m('Unavailable in Your Current Region')}
          cancelButtonAccessibilityLabel='play'
          messageAccessiblityLabel='cdd_warning_text'
          linkAccessibilityLabel='learn_more'
          message={''}
          buttonTitleArray={[_m('DEPOSIT'), _m('PLAY')]}
          callback={(index: number) => {
            ModalNavigationController.hideModal();

            if (index == 1) {
              AWSManager.trackEvent('CASH_DISABLED_LOCATION_MODAL_PLAY_NOW');
              this.switchToPlayTab(navigator);
              resolve(false);
              return;
            }

            AWSManager.trackEvent('CASH_DISABLED_LOCATION_MODAL_DEPOSIT');
            resolve(true);
          }}
        />
      );
      AWSManager.trackEvent('CASH_DISABLED_LOCATION_MODAL');
      ModalNavigationController.showModal(modal, {animationType: 'fade', transparent: true});
      Preferences.set({[SKZConstants.HAS_SEEN_CASH_DISABLED_LOCATION_WARNING]: true});
    });
  }

  switchToPlayTab(navigator: Navigator) {
    navigator.popToTop();
    GlobalEventEmitter.emit('switchToTab', 'PLAY');
  }

  _renderCustomView(): React.ReactNode {
    return (
      <Markdown style={MarkdownStyle}>
        {_m(
          "Skill-based cash gaming is not yet allowed in your current state/province/country. While you're still able to make a deposit, you'll only be able to play practice matches without a cash entry fee. [Learn More](https://skillz.zendesk.com/hc/en-us/articles/203685899-Who-is-eligible-to-compete-in-Skillz-competitions-)"
        )}
        .
      </Markdown>
    );
  }

  _locationModalCallback = (index: number) => {
    if (index == 1) {
      LoadingIndicator.show();
      SkillzModule.goToAppSettings();
    }
    ModalNavigationController.hideModal();
  };
}

export {CashDisabledLocationController as default};
